import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { blogService, blogAdsService, type BlogPost, type BlogAd } from '../services/blogService';
import AdSnippet from '../components/AdSnippet';

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [featured, setFeatured] = useState<BlogPost | null>(null);
  const [ads, setAds] = useState<BlogAd[]>([]);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [allPosts, featuredPost, activeAds] = await Promise.all([
          blogService.getPublishedPosts({ lang: 'fa' }),
          blogService.getFeaturedPost(),
          blogAdsService.getActiveAds(),
        ]);
        setPosts(allPosts);
        setFeatured(featuredPost);
        setAds(activeAds);
      } catch (err: any) {
        console.error('Error loading blog data', err);
        setError(err.message || 'خطا در بارگذاری بلاگ');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    posts.forEach((p) => {
      (p.tags || []).forEach((t) => set.add(t));
    });
    return Array.from(set);
  }, [posts]);

  const filteredPosts = useMemo(() => {
    if (!selectedTag) return posts;
    return posts.filter((p) => (p.tags || []).includes(selectedTag));
  }, [posts, selectedTag]);

  const heroAds = ads.filter((a) => a.position === 'hero' || a.position === 'special');
  const sidebarAds = ads.filter((a) => a.position === 'sidebar');

  return (
    <>
      <Helmet>
        <title>بلاگ اکتیو لجند | Active Legend Blog</title>
        <meta
          name="description"
          content="آخرین مقالات، اخبار و devlogهای استودیو بازی‌سازی اکتیو لجند (Active Legend). آموزش ساخت بازی، معرفی بازی‌ها و تکنولوژی‌های مورد استفاده."
        />
        <link rel="canonical" href="https://activelegend.ir/blog" />
        <meta property="og:title" content="بلاگ اکتیو لجند | Active Legend Blog" />
        <meta
          property="og:description"
          content="مقالات و نوشته‌های تیم اکتیو لجند درباره بازی‌ها، توسعه بازی و اخبار استودیو."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://activelegend.ir/blog" />
        <meta property="og:image" content="https://activelegend.ir/AE%20logo.svg" />
      </Helmet>
      <div className="min-h-screen bg-black pt-20 md:pt-24 pb-12 md:pb-16 overflow-x-hidden" dir="rtl">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-right">
          <header className="mb-8 md:mb-10 text-right">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl font-black text-white mb-3">
              بلاگ و مجله گیمینگ اکتیو لجند
            </h1>
            <p className="text-zinc-300 text-sm sm:text-base max-w-2xl text-right leading-relaxed">
              تحلیل‌های تخصصی، بررسی بازی‌های استیم، آنریل انجین ۵ و اخبار تکنولوژی استودیو Active Legend.
            </p>
          </header>

          {loading && (
            <div className="text-center text-gray-300 py-10">در حال بارگذاری...</div>
          )}
          {error && !loading && (
            <div className="text-center text-red-400 py-4">{error}</div>
          )}

          {!loading && !error && (
            <>
              {/* Featured + hero ads */}
              {(featured || heroAds.length > 0) && (
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 md:mb-10 text-right">
                  <div className="lg:col-span-2 bg-zinc-900/90 border border-white/15 rounded-2xl sm:rounded-3xl p-6 sm:p-7 shadow-2xl min-w-0">
                    <h2 className="text-base font-bold text-primary mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                      <span>مقاله ویژه تحریریه</span>
                    </h2>
                    {featured ? (
                      <Link to={`/blog/${featured.slug}`} className="group block">
                        <div className="flex flex-col md:flex-row gap-5 items-center">
                          {featured.cover_image_url && (
                            <img
                              src={featured.cover_image_url}
                              alt={featured.title}
                              className="w-full md:w-56 h-48 object-cover rounded-2xl border border-white/10 group-hover:scale-105 transition-transform"
                            />
                          )}
                          <div className="flex-1">
                            <h3 className="text-xl md:text-2xl font-black text-white group-hover:text-primary transition-colors mb-3">
                              {featured.title}
                            </h3>
                            <p className="text-zinc-300 text-sm md:text-base mb-4 line-clamp-3 leading-relaxed">
                              {featured.excerpt}
                            </p>
                            <span className="inline-flex items-center gap-1.5 text-primary text-sm font-bold">
                              <span>مطالعه کامل تحلیل</span>
                              <span>←</span>
                            </span>
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <p className="text-gray-400 text-sm">هنوز پست ویژه‌ای تنظیم نشده است.</p>
                    )}
                  </div>
                  <aside className="space-y-4 min-w-0">
                    {/* جایگاه یکتانت کنار پست ویژه */}
                    <div id="yk-blog-hero" className="w-full flex justify-center items-center" />
                    {heroAds.map((ad) => (
                      <AdSnippet
                        key={ad.id}
                        html={ad.html_snippet}
                        position={ad.position}
                        className={ad.position === 'special' ? 'sponsor-box sponsor-box-text sponsor-box-special' : 'sponsor-box sponsor-box-text'}
                      />
                    ))}
                    {sidebarAds.map((ad) => (
                      <AdSnippet
                        key={ad.id}
                        html={ad.html_snippet}
                        className="sponsor-box sponsor-box-text sponsor-box-sidebar"
                      />
                    ))}
                  </aside>
                </section>
              )}

              {/* Tags filter */}
              {allTags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  <button
                    onClick={() => setSelectedTag(null)}
                    className={`px-3 py-1 rounded-full text-xs md:text-sm border ${
                      !selectedTag
                        ? 'bg-primary text-black border-primary'
                        : 'bg-transparent text-gray-300 border-white/20'
                    }`}
                  >
                    همه تگ‌ها
                  </button>
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setSelectedTag(tag)}
                      className={`px-3 py-1 rounded-full text-xs md:text-sm border ${
                        selectedTag === tag
                          ? 'bg-primary text-black border-primary'
                          : 'bg-transparent text-gray-300 border-white/20'
                      }`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              )}

              {/* Posts list */}
              <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5 sm:gap-6 text-right">
                {filteredPosts.map((post) => (
                  <article
                    key={post.id}
                    className="bg-zinc-900/80 border border-white/10 hover:border-primary/40 rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-xl flex flex-col min-w-0 overflow-hidden transition-all hover:-translate-y-1"
                  >
                    <Link to={`/blog/${post.slug}`} className="group block">
                      {post.cover_image_url && (
                        <div className="overflow-hidden rounded-2xl mb-4 bg-zinc-950 aspect-video">
                          <img
                            src={post.cover_image_url}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <h2 className="text-lg md:text-xl font-bold text-white group-hover:text-primary transition-colors mb-2.5 line-clamp-2 leading-snug">
                        {post.title}
                      </h2>
                    </Link>
                    <p className="text-sm text-zinc-300 mb-4 line-clamp-3 leading-relaxed text-justify font-normal">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto flex items-center justify-between pt-3 border-t border-white/10">
                      <div className="flex flex-wrap gap-1.5">
                        {(post.tags || []).map((tag) => (
                          <button
                            key={tag}
                            onClick={() => setSelectedTag(tag)}
                            className="text-[11px] px-2.5 py-0.5 rounded-full bg-zinc-800 text-zinc-300 hover:text-primary border border-white/10"
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="text-primary hover:text-white text-xs font-bold transition-colors"
                      >
                        مطالعه مقاله ←
                      </Link>
                    </div>
                  </article>
                ))}
                {!filteredPosts.length && (
                  <p className="text-gray-400 text-sm col-span-full">
                    هنوز پستی برای این تگ ثبت نشده است.
                  </p>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </>
  );
}

